// Detects a deliberate long press. Used for the hidden maintenance entry, which must
// not be reachable by anything a person on shift would do by accident.

export const useLongPress = (onLongPress: () => void, delayMs = 800) => {
  const pressing = ref(false)

  let timer: ReturnType<typeof setTimeout> | null = null
  let startX = 0
  let startY = 0

  const cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }

    pressing.value = false
  }

  // Unmounting is the one thing that does not cancel on its own. A press still held when
  // the component goes away — navigating off mid-hold — gets no pointerup, and removing
  // the element does not reliably fire pointerleave either, so the timer would outlive
  // its component and fire into nothing. Harmless while the callbacks only set local
  // refs, but not worth leaving for the first one that does more than that.
  //
  // onScopeDispose rather than onUnmounted so this still holds if the composable is used
  // inside a standalone effect scope rather than a component.
  onScopeDispose(cancel)

  const start = (event: PointerEvent) => {
    cancel()

    startX = event.clientX
    startY = event.clientY
    pressing.value = true

    timer = setTimeout(() => {
      timer = null
      pressing.value = false
      onLongPress()
    }, delayMs)
  }

  // A finger never holds perfectly still, and neither does a hand on a mouse. Only a
  // real drag cancels, not the wobble of holding something down.
  const move = (event: PointerEvent) => {
    if (!timer) {
      return
    }

    const moved = Math.hypot(event.clientX - startX, event.clientY - startY)

    if (moved > 12) {
      cancel()
    }
  }

  return {
    pressing,
    handlers: {
      pointerdown: start,
      pointerup: cancel,
      pointercancel: cancel,
      pointerleave: cancel,
      pointermove: move,
      // A long press on iOS otherwise raises the copy and share menu over the app.
      contextmenu: (event: Event) => event.preventDefault(),
    },
  }
}
